import { useEffect, useState } from "react";
import { doc, onSnapshot, Timestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, firestore } from "config/firebaseConfig";

/**
 * Live flight-status view model, mirrored from the backend's Firestore doc at
 * `flightStatus/{uid}/requests/{requestId}`. Every field is optional — the
 * server only writes the fields the latest poll actually returned, so read
 * everything defensively. Timestamp fields are converted to native `Date`s.
 */
export interface LiveFlightStatus {
  flightNumber?: string;
  source?: string;
  destination?: string;
  departureDate?: Date;
  flightState?: string;
  scheduledDeparture?: Date;
  estimatedDeparture?: Date;
  actualDeparture?: Date;
  scheduledArrival?: Date;
  estimatedArrival?: Date;
  actualArrival?: Date;
  departureTerminal?: string;
  arrivalTerminal?: string;
  departureGate?: string;
  arrivalGate?: string;
  arrivalBaggageBelt?: string;
  updatedAt?: Date;
}

const STRING_FIELDS = [
  "flightNumber",
  "source",
  "destination",
  "flightState",
  "departureTerminal",
  "arrivalTerminal",
  "departureGate",
  "arrivalGate",
  "arrivalBaggageBelt",
] as const;

const DATE_FIELDS = [
  "departureDate",
  "scheduledDeparture",
  "estimatedDeparture",
  "actualDeparture",
  "scheduledArrival",
  "estimatedArrival",
  "actualArrival",
  "updatedAt",
] as const;

/** Best-effort conversion of a Firestore Timestamp (or date-ish value) to Date. */
const toDate = (value: unknown): Date | undefined => {
  if (value == null) return undefined;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof (value as any)?.toDate === "function") return (value as any).toDate();
  if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
};

/**
 * Map a raw Firestore doc into a partial view model containing ONLY the fields
 * that are actually present. Absent fields are omitted (not set to undefined) so
 * that merging into existing state never clobbers a previously-known value.
 */
const mapSnapshotData = (data: any): Partial<LiveFlightStatus> => {
  const result: Partial<LiveFlightStatus> = {};
  if (!data) return result;

  for (const key of STRING_FIELDS) {
    const v = data[key];
    if (v != null && v !== "") (result as any)[key] = String(v);
  }
  for (const key of DATE_FIELDS) {
    const d = toDate(data[key]);
    if (d) (result as any)[key] = d;
  }
  return result;
};

/** A flight in one of these states won't change again — stop listening. */
const isTerminalState = (state?: string): boolean => {
  if (!state) return false;
  const s = state.toLowerCase();
  return s === "landed" || s === "cancelled" || s === "canceled";
};

/**
 * Subscribe to live flight-status updates for a single load request.
 *
 * Attach a listener only while `enabled` is true (the caller passes
 * `isEnabledStatus === true` for an opened request); the subscription is torn
 * down automatically when the component unmounts, when `enabled` flips to false,
 * or once the flight lands / is cancelled.
 *
 * Returns `null` until the backend has written a doc for this request. A missing
 * doc means "no live info yet" — callers should fall back to the schedule from
 * GET /UserRequests.
 *
 * @param requestId LoadRequest.id (same id sent to POST /LoadRequest/EnableUpdate)
 * @param enabled   whether a listener should currently be attached
 */
export function useFlightStatus(
  requestId: number | undefined | null,
  enabled: boolean,
): { status: LiveFlightStatus | null } {
  const [status, setStatus] = useState<LiveFlightStatus | null>(null);
  const [uid, setUid] = useState<string | null>(
    () => auth.currentUser?.uid ?? null,
  );

  // Resolve the current user's uid. auth.currentUser can still be null on the
  // first render while Firebase auth initialises, so fall back to the listener.
  useEffect(() => {
    if (auth.currentUser?.uid) {
      setUid(auth.currentUser.uid);
      return;
    }
    const unsub = onAuthStateChanged(auth, (user) => setUid(user?.uid ?? null));
    return () => unsub();
  }, []);

  useEffect(() => {
    // Clear any stale status so live data from a previously-open request can't
    // leak onto a different row once the listener is torn down.
    if (!enabled || !uid || requestId == null) {
      setStatus(null);
      return;
    }

    const ref = doc(
      firestore,
      "flightStatus",
      uid,
      "requests",
      String(requestId),
    );

    let stopped = false;
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        // The doc only appears after the server's first meaningful change. Until
        // then keep null so the caller uses the schedule.
        if (!snap.exists()) return;

        const incoming = mapSnapshotData(snap.data());
        // Merge — fields absent from the latest poll keep their prior values.
        setStatus((prev) => ({ ...(prev ?? {}), ...incoming }));

        // Once the flight has landed / been cancelled no further updates are
        // coming, so detach the listener but keep the final status on screen.
        if (!stopped && isTerminalState(incoming.flightState)) {
          stopped = true;
          unsubscribe();
        }
      },
      (error) => {
        console.error("Error in flight-status listener:", error);
      },
    );

    return () => {
      stopped = true;
      unsubscribe();
    };
  }, [enabled, uid, requestId]);

  return { status };
}
